<template>
    <el-drawer :title="form.code ? '编辑配网方案' : '新增配网方案'" v-model="visible" direction="rtl" size="760px"
        custom-class="flex-drawer" :close-on-click-modal="false">
        <div class="drawer-layout">
            <div class="drawer-content">
                <el-form ref="formRef" :model="form" :rules="rules" label-width="110px" size="small">
                    <div class="section-title">基本信息</div>
                    <!-- 新增/复制时可选择所属配网型号，例如把 GC3 的方案复制给由它改款的 GC4 -->
                    <el-form-item v-if="!form.code" label="所属配网型号" prop="pairing_model_code"
                        :rules="[{ required: true, message: '请选择配网型号', trigger: 'change' }]">
                        <el-select v-model="form.pairing_model_code" filterable style="width: 100%;"
                            @change="onPairingModelChange">
                            <el-option v-for="m in pairingModels" :key="m.code" :label="m.model" :value="m.code"></el-option>
                        </el-select>
                    </el-form-item>
                    <el-form-item v-else label="所属配网型号">{{ pairingModel.model }}</el-form-item>
                    <el-form-item label="方案名称" prop="name">
                        <el-input v-model="form.name" placeholder="App 列表展示名，如 GC2 (BLE+WiFi 2.4G)"></el-input>
                    </el-form-item>
                    <el-form-item label="多语言名称">
                        <el-select v-model="form.i18n_key" clearable filterable placeholder="不使用，直接显示方案名称"
                            style="width: 100%;">
                            <el-option v-for="t in textOptions('scheme_name')" :key="t.code" :label="t.label"
                                :value="t.code"></el-option>
                        </el-select>
                        <div class="form-tip">从文案库选择（用途：方案名），App 按语言显示，代替方案名称
                            <el-button type="text" size="mini" @click="manageTexts('scheme_name')">管理文案</el-button>
                        </div>
                    </el-form-item>
                    <el-form-item label="备注">
                        <el-input v-model="form.description"></el-input>
                    </el-form-item>
                    <!-- modelId 只有蓝牙配网才有，勾选蓝牙配网后显示 -->
                    <el-form-item v-if="isBle" label="蓝牙匹配">
                        <el-switch v-model="form.ble_match" active-text="参与 App 蓝牙扫描匹配"></el-switch>
                        <div class="form-tip">关闭后不计算 modelId，App 蓝牙扫描不会按它识别设备。用于走原生配网（如涂鸦插座、灯泡）的蓝牙设备</div>
                    </el-form-item>
                    <el-form-item v-if="isBle && form.ble_match" label="modelId" prop="model_id_override"
                        :rules="[{ pattern: /^[0-9a-f]{4}$/, message: '需为 4 位小写十六进制，如 05e1', trigger: 'blur' }]">
                        <span class="model-id">{{ form.model_id_override || autoModelId }}</span>
                        <span class="form-tip">
                            {{ form.model_id_override ? '已覆盖，' : '' }}默认由配网型号「{{ pairingModel.model }}」计算 CRC16
                        </span>
                        <el-input v-model="form.model_id_override" placeholder="仅历史固件需要覆盖，留空使用自动值"
                            style="margin-top: 6px;" clearable></el-input>
                    </el-form-item>
                    <el-form-item label="展示控制">
                        <el-checkbox v-model="form.test_only">仅测试用户可见</el-checkbox>
                        <el-checkbox v-model="form.hide_in_list">隐藏</el-checkbox>
                        <div class="form-tip">隐藏后不出现在手动添加列表和设备帮助页，只在蓝牙扫描到对应 modelId 时使用，用于兼容 modelId 不同的历史固件</div>
                    </el-form-item>
                    <el-form-item label="版本选择提示">
                        <el-select v-model="form.group_tip" clearable filterable
                            placeholder="默认：识别您的设备版本，然后选择匹配的版本。" style="width: 100%;">
                            <el-option v-for="t in textOptions('group_tip')" :key="t.code" :label="t.label"
                                :value="t.code"></el-option>
                        </el-select>
                        <div class="form-tip">同一配网型号有多个方案时，App 会弹出版本选择，这里是弹窗上的提示文案（文案库用途：版本选择提示），取该型号第一个方案的设置
                            <el-button type="text" size="mini" @click="manageTexts('group_tip')">管理文案</el-button>
                        </div>
                    </el-form-item>

                    <div class="section-title">配网功能</div>
                    <el-form-item label="配网方式" prop="functions" required>
                        <el-checkbox-group v-model="form.functions">
                            <el-checkbox v-for="f in connectionFunctions" :key="f.code" :label="f.code">{{ f.name }}</el-checkbox>
                        </el-checkbox-group>
                    </el-form-item>
                    <el-form-item label="设备特性">
                        <el-checkbox-group v-model="form.functions">
                            <el-checkbox v-for="f in featureFunctions" :key="f.code" :label="f.code">{{ f.name }}</el-checkbox>
                        </el-checkbox-group>
                    </el-form-item>

                    <div class="section-title">图片</div>
                    <el-form-item label="图片" prop="images" required>
                        <div class="image-list">
                            <div class="form-tip">左视图（列表图）必填，用于 App 型号列表；其余未配置时 App 使用默认图</div>
                            <div v-for="(img, index) in form.images" :key="index" class="image-row">
                                <el-select v-model="img.type" size="small" style="width: 150px; flex-shrink: 0;">
                                    <el-option v-for="t in imageTypes" :key="t.value" :label="t.label" :value="t.value"></el-option>
                                </el-select>
                                <el-input v-model="img.url" size="small" placeholder="图片地址"></el-input>
                                <el-image v-if="img.url" :src="img.url" fit="contain" class="image-thumb"
                                    :preview-src-list="[img.url]"></el-image>
                                <el-button type="text" size="small" style="color: #f56c6c;"
                                    @click="form.images.splice(index, 1)">删除</el-button>
                            </div>
                            <el-button size="small" plain icon="el-icon-plus" @click="addImage">添加图片</el-button>
                        </div>
                    </el-form-item>

                    <div class="section-title">
                        引导文案
                        <el-button type="text" size="mini" class="title-link" @click="manageTexts()">管理文案</el-button>
                    </div>
                    <el-form-item v-for="g in guideFields" :key="g.key" :label="g.label">
                        <el-select v-model="form.guide[g.key]" clearable filterable placeholder="不展示" style="width: 100%;"
                           >
                            <el-option v-for="t in textOptions(g.key)" :key="t.code" :label="t.label"
                                :value="t.code"></el-option>
                        </el-select>
                    </el-form-item>

                    <div class="section-title">指示灯</div>
                    <el-form-item label="指示灯">
                        <el-switch v-model="form.led_config.has_led" active-text="有指示灯" @change="onLedToggle"></el-switch>
                    </el-form-item>
                    <template v-if="form.led_config.has_led">
                        <el-form-item label="复位颜色" prop="led_config.reset_color"
                            :rules="[{ required: true, message: '请选择复位颜色', trigger: 'change' }]">
                            <el-select v-model="form.led_config.reset_color" placeholder="请选择" style="width: 200px;">
                                <template #prefix>
                                    <span class="select-prefix">
                                        <LedDot :colors="currentResetColor ? [currentResetColor] : []" :size="12"></LedDot>
                                    </span>
                                </template>
                                <el-option v-for="c in resetColorOptions" :key="c.value" :label="c.label" :value="c.value">
                                    <span class="option-flex">
                                        <LedDot :colors="c.color ? [c.color] : []" :size="12"></LedDot>
                                        <span>{{ c.label }}</span>
                                        <span class="color-code">{{ c.value }}</span>
                                    </span>
                                </el-option>
                            </el-select>
                        </el-form-item>
                        <el-form-item label="灯效说明" prop="led_config.status" :rules="[{ validator: validateLed, trigger: 'change' }]">
                            <div class="led-list">
                                <div v-for="(st, index) in form.led_config.status" :key="index" class="led-item">
                                    <div class="led-row">
                                        <LedDot :colors="effectOf(st.code).colors" :interval="effectOf(st.code).interval"></LedDot>
                                        <el-select v-model="st.code" size="small" clearable filterable placeholder="灯灭"
                                            class="led-code">
                                            <el-option v-for="e in effectOptions(st.code)" :key="e.code" :label="e.name"
                                                :value="e.code">
                                                <span class="option-flex">
                                                    <LedDot :colors="e.colors" :interval="e.interval" :size="12"></LedDot>
                                                    <span>{{ e.name }}</span>
                                                    <span class="color-code">{{ e.code }}</span>
                                                </span>
                                            </el-option>
                                        </el-select>
                                        <el-input v-model="st.key" size="small" placeholder="文案 key（必填）"></el-input>
                                        <el-input v-model="st.description" size="small" placeholder="说明"></el-input>
                                        <el-button type="text" size="small" style="color: #f56c6c;"
                                            @click="form.led_config.status.splice(index, 1)">删除</el-button>
                                    </div>
                                    <div class="led-row led-text-row">
                                        <span class="led-label">标题</span>
                                        <TextRefSelect v-model="st.title" type="led_status" :texts="textGroups.led_status || []"
                                            default-placeholder="不使用，App 显示灯效动画的名称"></TextRefSelect>
                                    </div>
                                    <div class="led-text-row">
                                        <LedContentField :content="st.content" :actions="meta.led_actions || []"
                                            :action="st.action" :texts="textGroups.led_status || []"
                                            @update:content="st.content = $event"
                                            @update:action="st.action = $event"></LedContentField>
                                    </div>
                                </div>
                                <div>
                                    <el-button size="small" plain icon="el-icon-plus" @click="addLed">添加灯效</el-button>
                                    <el-select v-model="ledPreset" size="small" filterable placeholder="从预设添加"
                                        style="width: 360px; margin-left: 8px;" @change="addLedPreset">
                                        <el-option-group v-for="g in presetGroups" :key="g.value" :label="g.label">
                                            <el-option v-for="p in g.items" :key="p.index"
                                                :label="`${effectName(p.code)} → ${p.key}`" :value="p.index">
                                                <span class="option-flex">
                                                    <LedDot :colors="effectOf(p.code).colors" :interval="effectOf(p.code).interval"
                                                        :size="12"></LedDot>
                                                    <span>{{ effectName(p.code) }} → {{ p.key }}</span>
                                                </span>
                                            </el-option>
                                        </el-option-group>
                                    </el-select>
                                </div>
                                <div class="form-tip">灯效从字典「灯效动画」选择；标题、说明从文案库选择（用途：灯效说明），说明支持富文本，可按场景区分（已绑定、分享用户）
                                    <el-button type="text" size="mini" @click="manageTexts('led_status')">管理文案</el-button>
                                </div>
                            </div>
                        </el-form-item>
                    </template>
                </el-form>
            </div>
            <div class="drawer-footer">
                <el-button size="small" @click="visible = false">取消</el-button>
                <el-button type="primary" size="small" :loading="saving" @click="submit">保存</el-button>
            </div>
        </div>
    </el-drawer>
</template>

<script>
import pairingApi from './api'
import LedDot from './LedDot.vue'
import LedContentField from './LedContentField.vue'
import TextRefSelect from './TextRefSelect.vue'
import {
    crcModelId, imageTypes as metaImageTypes, guideFields as metaGuideFields, emptyScheme, groupTextsByType, textOptionLabel,
    validateTextRef, textRefDefault,
} from './utils'

// 深拷贝文案引用：字符串原样，对象拷贝一份，避免方案之间共享预设的对象
const copyRef = (ref) => (ref && typeof ref === 'object' ? JSON.parse(JSON.stringify(ref)) : ref || '')

export default {
    name: 'PairingSchemeEditor',
    components: { LedDot, LedContentField, TextRefSelect },
    props: {
        meta: { type: Object, required: true },
    },
    emits: ['saved', 'manage-texts'],
    data() {
        return {
            visible: false,
            saving: false,
            pairingModel: {},
            pairingModels: [],
            form: emptyScheme(''),
            ledPreset: null,
            textLoading: false,
            textGroups: {},
            rules: {
                name: [{ required: true, message: '请输入方案名称', trigger: 'blur' }],
                functions: [{ validator: this.validateFunctions, trigger: 'change' }],
                images: [{ validator: this.validateImages, trigger: 'change' }],
            },
        }
    },
    computed: {
        // 图片类型、引导文案项来自后台字典
        imageTypes() {
            return metaImageTypes(this.meta)
        },
        guideFields() {
            return metaGuideFields(this.meta)
        },
        connectionFunctions() {
            return (this.meta.functions || []).filter((f) => f.type === 'connection')
        },
        featureFunctions() {
            return (this.meta.functions || []).filter((f) => f.type === 'feature')
        },
        // App 只支持这几种复位颜色；已保存的值不在选项里时保留一项，避免显示成空白
        resetColorOptions() {
            const options = [...(this.meta.reset_colors || [])]
            const current = this.form.led_config && this.form.led_config.reset_color
            if (current && !options.some((c) => c.value === current)) {
                options.unshift({ value: current, label: `${current}（App 不支持）` })
            }
            return options
        },
        currentResetColor() {
            const current = this.form.led_config && this.form.led_config.reset_color
            const c = (this.meta.reset_colors || []).find((x) => x.value === current)
            return (c && c.color) || ''
        },
        // 灯效预设按设备类型（meta.led_groups）分组；未知分组的预设放在最后
        presetGroups() {
            const presets = (this.meta.led_status_presets || []).map((p, index) => ({ ...p, index }))
            const groups = (this.meta.led_groups || []).map((g) => ({
                value: g.value, label: g.label, items: presets.filter((p) => p.group === g.value),
            }))
            const known = groups.map((g) => g.value)
            const others = presets.filter((p) => !known.includes(p.group))
            if (others.length) groups.push({ value: '__other', label: '其他', items: others })
            return groups.filter((g) => g.items.length)
        },
        isBle() {
            return this.form.functions.includes('support_ble')
        },
        autoModelId() {
            return crcModelId(this.pairingModel.model)
        },
    },
    methods: {
        // 打开指示灯时默认红色（大多数设备），避免每次手动选择
        onLedToggle(on) {
            if (on && !this.form.led_config.reset_color) {
                this.form.led_config.reset_color = 'red'
            }
        },
        open(pairingModel, scheme) {
            this.pairingModel = pairingModel
            pairingApi.PairingModelList().then((res) => {
                this.pairingModels = res.data.data
            })
            this.form = scheme ? JSON.parse(JSON.stringify(scheme)) : emptyScheme(pairingModel.code)
            // 兼容没有 ble_match 的旧数据：蓝牙方案默认参与匹配
            if (this.form.ble_match === undefined) this.form.ble_match = this.form.functions.includes('support_ble')
            if (!scheme) this.form.images = [{ type: 'left', url: '', description: '' }]
            this.form.guide = this.form.guide || {}
            // 旧数据的 text 即说明正文，改名为 content；单频/双频由方案决定，说明就是该方案唯一的文案
            this.form.led_config.status = (this.form.led_config.status || []).map((st) => {
                const { text, ...rest } = st
                return {
                    ...rest,
                    title: rest.title || '',
                    content: rest.content || text || '',
                    action: rest.action || '',
                }
            })
            this.ledPreset = null
            this.loadTexts()
            this.visible = true
            this.$nextTick(() => this.$refs.formRef && this.$refs.formRef.clearValidate())
        },
        // 文案库一次取全量，按用途分组给各下拉使用
        loadTexts() {
            this.textLoading = true
            return pairingApi.TextList().then((res) => {
                this.textLoading = false
                this.textGroups = groupTextsByType(res.data.data)
            }).catch(() => {
                this.textLoading = false
            })
        },
        // 某用途的下拉选项；当前值不在文案库里（如历史 key）时也保留一项，避免下拉显示成空白
        textOptions(type) {
            const options = (this.textGroups[type] || []).map((t) => ({ code: t.code, label: textOptionLabel(t) }))
            const current = type === 'scheme_name' ? this.form.i18n_key
                : type === 'group_tip' ? this.form.group_tip
                    : (this.form.guide || {})[type]
            if (current && !options.some((o) => o.code === current)) {
                options.unshift({ code: current, label: `${current}（不在文案库中）` })
            }
            return options
        },
        // 灯效动画字典（meta.led_effects）；已保存的 code 不在字典里时保留一项
        effectOptions(current) {
            const options = (this.meta.led_effects || []).map((e) => ({ ...e, name: e.name || e.code }))
            if (current && !options.some((e) => e.code === current)) {
                options.unshift({ code: current, name: `${current}（不在字典中）`, colors: [], interval: 0 })
            }
            return options
        },
        effectOf(code) {
            const e = code && (this.meta.led_effects || []).find((x) => x.code === code)
            return e ? { colors: e.colors || [], interval: e.interval || 0 } : { colors: [], interval: 0 }
        },
        effectName(code) {
            if (!code) return '灯灭'
            const e = (this.meta.led_effects || []).find((x) => x.code === code)
            return (e && e.name) || code
        },
        manageTexts(type) {
            this.$emit('manage-texts', type)
        },
        // 换了配网型号：同步方案名称里的型号前缀；覆盖的 modelId 只对原型号的历史固件有效，清空
        onPairingModelChange(code) {
            const target = this.pairingModels.find((m) => m.code === code)
            if (!target) return
            const prev = this.pairingModel.model
            if (prev && this.form.name.startsWith(prev)) {
                this.form.name = target.model + this.form.name.slice(prev.length)
            }
            if (target.code !== this.pairingModel.code) this.form.model_id_override = ''
            this.pairingModel = target
        },
        validateFunctions(rule, value, callback) {
            const hasConnection = value.some((code) => this.connectionFunctions.some((f) => f.code === code))
            callback(hasConnection ? undefined : new Error('至少选择一种配网方式'))
        },
        validateImages(rule, value, callback) {
            const left = value.find((img) => img.type === 'left')
            if (!left || !left.url) return callback(new Error('请添加左视图（列表图）'))
            const types = value.filter((img) => img.url).map((img) => img.type)
            if (new Set(types).size !== types.length) return callback(new Error('同一类型图片只能有一张'))
            callback()
        },
        // 灯效 code 可为空（如设备休眠时灯灭），但文案 key 和说明必填；已绑定、分享用户说明可空
        validateLed(rule, value, callback) {
            if (!value.length) return callback(new Error('请至少添加一条灯效说明'))
            if (value.some((st) => !(st.key || '').trim())) return callback(new Error('灯效的文案 key 不能为空'))
            for (let i = 0; i < value.length; i++) {
                const st = value[i]
                if (!textRefDefault(st.content)) return callback(new Error(`第 ${i + 1} 条灯效：请选择说明`))
                const err = validateTextRef(st.title) || validateTextRef(st.content)
                if (err) return callback(new Error(`第 ${i + 1} 条灯效：${err}`))
            }
            callback()
        },
        addImage() {
            const used = this.form.images.map((img) => img.type)
            const types = this.imageTypes
            const type = ((types.find((t) => !used.includes(t.value)) || types[0]) || {}).value || ''
            this.form.images.push({ type, url: '', description: '' })
        },
        addLed() {
            this.form.led_config.status.push({
                code: '', key: '', description: '', title: '', content: '', action: '',
            })
        },
        addLedPreset(index) {
            const preset = this.meta.led_status_presets[index]
            if (preset) {
                const { code, key, description, title, content, action } = preset
                this.form.led_config.status.push({
                    code: code || '', key: key || '', description: description || '', title: copyRef(title),
                    content: copyRef(content), action: action || '',
                })
            }
            this.$nextTick(() => {
                this.ledPreset = null
            })
        },
        submit() {
            this.$refs.formRef.validate((valid) => {
                if (!valid) return
                const data = JSON.parse(JSON.stringify(this.form))
                data.images = data.images.filter((img) => img.url)
                data.images.forEach((img) => {
                    img.description = img.description || (this.imageTypes.find((t) => t.value === img.type) || {}).label || ''
                })
                // modelId 由前端算好提交：蓝牙方案取覆盖值或配网型号名的 CRC16，非蓝牙方案为空
                if (data.functions.includes('support_ble') && data.ble_match) {
                    data.model_id = data.model_id_override || this.autoModelId
                } else {
                    if (!data.functions.includes('support_ble')) data.ble_match = false
                    data.model_id_override = ''
                    data.model_id = ''
                }
                if (!data.led_config.has_led) data.led_config = { has_led: false, reset_color: '', status: [] }
                Object.keys(data.guide).forEach((k) => !data.guide[k] && delete data.guide[k])
                this.saving = true
                pairingApi.PairingSchemeSave(data).then((res) => {
                    this.saving = false
                    if (res.data.code == 200) {
                        this.$message.success('保存成功')
                        this.visible = false
                        this.$emit('saved', data.pairing_model_code)
                        return
                    }
                    this.$message.error('保存失败' + res.data.msg)
                })
            })
        },
    },
}
</script>

<style scoped>
.drawer-layout {
    display: flex;
    flex-direction: column;
    height: 100%;
}

.drawer-content {
    flex: 1;
    overflow: auto;
    padding: 0 20px;
}

.drawer-footer {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    padding: 12px 20px;
    border-top: 1px solid #e4e7ed;
}

.section-title {
    margin: 16px 0 12px;
    padding-left: 8px;
    border-left: 3px solid #409eff;
    font-size: 14px;
    font-weight: 500;
    line-height: 16px;
    color: #303133;
}

.title-link {
    margin-left: 8px;
    padding: 0;
    min-height: 0;
    font-weight: normal;
}

.section-title:first-child {
    margin-top: 0;
}

.model-id {
    font-family: monospace;
    margin-right: 10px;
}

.form-tip {
    font-size: 12px;
    line-height: 18px;
    color: #909399;
}

.image-row,
.led-row {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;
}

.image-thumb {
    width: 36px;
    height: 36px;
    flex-shrink: 0;
    border: 1px solid #e4e7ed;
    border-radius: 3px;
}

.image-list {
    width: 100%;
}

/* 说明作为第一行，行高与 small 表单的 label（32px）一致，才能和"图片"文字对齐 */
.image-list .form-tip {
    line-height: 32px;
}

.color-code {
    float: right;
    margin-left: 12px;
    color: #909399;
    font-size: 12px;
}
.led-list {
    width: 100%;
}

.led-item {
    margin-bottom: 10px;
    padding-bottom: 2px;
    border-bottom: 1px dashed #ebeef5;
}

.led-item .led-row {
    margin-bottom: 6px;
}

.led-code {
    width: 200px;
    flex-shrink: 0;
}

/* 文案行与上一行的下拉对齐（预览圆点 14px + gap 8px） */
.led-text-row {
    align-items: flex-start;
    padding-left: 22px;
    padding-right: 36px;
}

.led-label {
    flex-shrink: 0;
    width: 28px;
    font-size: 12px;
    line-height: 32px;
    color: #606266;
}

.option-flex {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    width: 100%;
}

.option-flex .color-code {
    margin-left: auto;
}

.select-prefix {
    display: inline-flex;
    align-items: center;
    height: 100%;
    padding-left: 4px;
}
</style>

<style>
/* el-drawer__body 是 flex:1 子项，默认 min-height:auto 会被内容撑高，抽屉又是 overflow:hidden，导致无法滚动。
   限制其高度后由内部 .drawer-content 滚动；抽屉通过 custom-class 命中，不影响其他页面 */
.flex-drawer .el-drawer__body {
    min-height: 0;
    overflow: hidden;
}
</style>
